import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight, BadgeCheck } from "lucide-react";
import SEO from "../components/SEO";
import InquiryForm from "../components/InquiryForm";
import TherapyBookingButton from "../components/TherapyBookingButton";
import { API } from "../lib/api";

const SKILLS = [
  "Emotional Regulation",
  "Behaviour Support",
  "Focus & Attention",
  "Social Skills",
  "Confidence & Independence",
  "Executive Functioning",
  "Routines & Transitions",
  "Communication",
];

const WHO_LIST = [
  "Emotional regulation",
  "Behaviour challenges",
  "Focus and attention",
  "Following instructions",
  "Social skills",
  "Confidence building",
  "Communication",
  "Transitions and routines",
  "Turn-taking and sharing",
  "Frustration tolerance",
  "Classroom readiness",
  "Executive functioning skills",
];

const SESSION_FORMAT = [
  "1-on-1 individualized sessions",
  "Structured play-based activities",
  "Goal-focused support",
  "Parent feedback",
  "Practical strategies for home and learning environments",
];

const SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "services", label: "Our Services" },
  { id: "practitioners", label: "Our Practitioners" },
  { id: "how-it-works", label: "How It Works" },
  { id: "who-we-support", label: "Who We Support" },
  { id: "skills", label: "Skills We Work On" },
  { id: "insurance", label: "Insurance & Payment" },
  { id: "autism-funding", label: "Autism Funding" },
  { id: "contact", label: "Contact" },
];

const HOW_IT_WORKS = [
  { step: "1", title: "Explore our services", text: "Learn about Nexora's Therapy & Support services and what each one focuses on." },
  { step: "2", title: "Review practitioner information", text: "Read practitioner profiles to find the right fit for your child." },
  { step: "3", title: "Select Book Appointment", text: "Use the booking button on a service or practitioner profile." },
  { step: "4", title: "Continue to Jane", text: "You'll continue to our secure Jane booking site to choose a time." },
  { step: "5", title: "Complete everything in Jane", text: "Appointment selection, intake, consent, and payment are all handled in Jane as applicable." },
];

export default function PlayBasedSupportPage() {
  const [therapy, setTherapy] = useState({ practitioners: [], services: [], booking: null });

  useEffect(() => {
    fetch(`${API}/site/therapy`)
      .then((r) => r.json())
      .then((d) => setTherapy({ practitioners: d.practitioners || [], services: d.services || [], booking: d.booking || null }))
      .catch(() => {});
  }, []);

  const { practitioners, services, booking } = therapy;
  const practitionerById = (pid) => practitioners.find((p) => p.id === pid);

  return (
    <>
      <SEO
        path="/play-based-support"
        title="Therapy & Play-Based Support for Children | Nexora"
        description="Individualized therapy and play-based support in Richmond BC — emotional regulation, focus, behaviour, social skills, and confidence. Meet our practitioners and book an appointment."
      />

      {/* Hero */}
      <section
        className="relative overflow-hidden bg-[#FFFCF7]"
        data-testid="play-based-hero"
      >
        <div className="absolute -top-32 -right-24 w-[34rem] h-[34rem] rounded-full bg-sky-100/60 blur-[120px] opacity-70 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20 pb-12 relative">
          <p className="eyebrow">Therapy & Support</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-extrabold text-slate-900 mt-4 max-w-3xl leading-[1.05]">
            Therapy & Play-Based Support
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            Individualized, welcoming support that helps children practise
            emotional regulation, focus, social skills, and confidence — guided
            by caring practitioners, one child at a time.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <TherapyBookingButton booking={booking} testId="therapy-hero-book-btn" />
          </div>

          {/* sub nav */}
          <nav
            className="mt-10 flex flex-wrap gap-2"
            data-testid="play-based-subnav"
          >
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="pill border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
              >
                {s.label}
              </a>
            ))}
          </nav>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 grid gap-16">
          {/* Overview */}
          <article id="overview" data-testid="section-overview">
            <p className="eyebrow">Overview</p>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-3">
              What this program is.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Play-Based Behaviour & Emotional Support is a 1-on-1 developmental
              support program for children who may need help with emotional
              regulation, behaviour, focus, confidence, social interaction, and
              routines.
            </p>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Every child is unique, so our approach is individualized: through
              structured play and guided activities, children practise practical
              skills in a supportive environment at their own pace.
            </p>
            <p className="mt-3 text-xs text-slate-500 leading-relaxed">
              This program is educational and developmental. It is not medical
              treatment and does not provide diagnosis.
            </p>
          </article>

          {/* Our Services */}
          <article id="services" data-testid="section-services">
            <p className="eyebrow">Our services</p>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-3">
              Therapy & Support services.
            </h2>
            {services.length === 0 ? (
              <p className="mt-4 text-sm text-slate-600" data-testid="services-empty">
                Our service listings are being finalized. Please{" "}
                <Link to="/contact" className="font-semibold text-[#0369A1]">contact admissions</Link>{" "}
                and we'll help you find the right support for your child.
              </p>
            ) : (
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                {services.map((s) => (
                  <div key={s.id} className="flex flex-col rounded-[1.5rem] bg-white border border-slate-200/80 p-5" data-testid={`service-card-${s.id}`}>
                    <h3 className="font-display font-extrabold tracking-tight" style={{ color: "#123B6D" }}>{s.title}</h3>
                    {s.age_group && <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mt-1">{s.age_group}</p>}
                    {s.description && <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{s.description}</p>}
                    {(s.practitioner_ids || []).length > 0 && (
                      <p className="mt-2 text-xs text-slate-500">
                        With: {(s.practitioner_ids || []).map((pid) => practitionerById(pid)?.name).filter(Boolean).join(", ")}
                      </p>
                    )}
                    <div className="mt-4">
                      <TherapyBookingButton
                        booking={booking}
                        serviceId={s.id}
                        practitionerId={(s.practitioner_ids || [])[0]}
                        size="sm"
                        testId={`service-book-btn-${s.id}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </article>

          {/* Our Practitioners */}
          <article id="practitioners" data-testid="section-practitioners">
            <p className="eyebrow">Meet our practitioners</p>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-3">
              Caring professionals, here for your child.
            </h2>
            {practitioners.length === 0 ? (
              <p className="mt-4 text-sm text-slate-600" data-testid="practitioners-empty">
                Practitioner profiles will be published here soon. In the
                meantime, please{" "}
                <Link to="/contact" className="font-semibold text-[#0369A1]">contact admissions</Link>{" "}
                with any questions about our team.
              </p>
            ) : (
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                {practitioners.map((p) => (
                  <div key={p.id} className="flex gap-4 rounded-[1.5rem] bg-white border border-slate-200/80 p-5" data-testid={`practitioner-card-${p.id}`}>
                    <div className="h-20 w-20 shrink-0 rounded-2xl overflow-hidden border border-slate-200 bg-[#EAF7FB]">
                      {p.photo_url ? (
                        <img src={`${API.replace(/\/api$/, "")}${p.photo_url}`} alt={p.name} loading="lazy" className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center font-display text-xl font-extrabold" style={{ color: "#123B6D" }}>
                          {p.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-display font-extrabold tracking-tight truncate" style={{ color: "#123B6D" }}>{p.name}</h3>
                      {(p.title || p.designation) && (
                        <p className="text-xs font-bold text-[#00A6B2] truncate">{[p.title, p.designation].filter(Boolean).join(" · ")}</p>
                      )}
                      {p.registration_number && (
                        <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-slate-500">
                          <BadgeCheck className="h-3 w-3 text-[#6DBE45]" /> Reg. {p.registration_number}
                        </p>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Link
                          to={`/play-based-support/practitioners/${p.id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
                          data-testid={`practitioner-view-${p.id}`}
                        >
                          View Profile
                        </Link>
                        <TherapyBookingButton booking={booking} practitionerId={p.id} size="sm" testId={`practitioner-book-btn-${p.id}`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </article>

          {/* How it works */}
          <article id="how-it-works" data-testid="section-how-it-works">
            <p className="eyebrow">How it works</p>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-3">
              Booking is simple.
            </h2>
            <ol className="mt-5 grid gap-3">
              {HOW_IT_WORKS.map((h) => (
                <li key={h.step} className="flex items-start gap-4 rounded-2xl bg-white border border-slate-200/70 p-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-display font-extrabold text-sm text-white" style={{ backgroundColor: "#00A6B2" }}>
                    {h.step}
                  </span>
                  <span>
                    <span className="block text-sm font-bold text-slate-900">{h.title}</span>
                    <span className="block text-sm text-slate-600 leading-relaxed">{h.text}</span>
                  </span>
                </li>
              ))}
            </ol>
            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              You won't be asked to fill out duplicate booking or intake forms on
              this website — everything is completed in Jane.
            </p>
          </article>

          {/* Who we support */}
          <article id="who-we-support" data-testid="section-who-we-support">
            <p className="eyebrow">Who we support</p>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-3">
              This program may help children needing support with:
            </h2>
            <ul className="mt-5 grid sm:grid-cols-2 gap-2">
              {WHO_LIST.map((w) => (
                <li
                  key={w}
                  className="flex items-start gap-3 rounded-2xl bg-white border border-slate-200/70 p-3.5"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-50">
                    <Check className="h-3 w-3 text-[#0369A1]" />
                  </span>
                  <span className="text-sm text-slate-700">{w}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm text-slate-600">
              Families may contact us whether or not their child has a formal
              diagnosis.
            </p>
          </article>

          {/* Skills */}
          <article id="skills" data-testid="section-skills">
            <p className="eyebrow">Skills we work on</p>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-3">
              Areas we focus on during sessions.
            </h2>
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              {SKILLS.map((s, i) => (
                <motion.div
                  key={s}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: i * 0.03 }}
                  className="rounded-2xl bg-white border border-slate-200/70 p-4 font-display font-bold text-slate-900"
                  data-testid={`skill-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {s}
                </motion.div>
              ))}
            </div>
          </article>

          {/* Session format */}
          <article id="session-format" data-testid="section-session-format">
            <p className="eyebrow">Session format</p>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-3">
              What sessions look like.
            </h2>
            <ul className="mt-5 grid gap-2">
              {SESSION_FORMAT.map((s) => (
                <li
                  key={s}
                  className="flex items-start gap-3 rounded-2xl bg-white border border-slate-200/70 p-4"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FFE6CC]">
                    <Check className="h-3.5 w-3.5 text-[#9A3412]" />
                  </span>
                  <span className="text-sm text-slate-700 leading-relaxed">
                    {s}
                  </span>
                </li>
              ))}
            </ul>
          </article>

          {/* Parent communication */}
          <article id="parent-communication" data-testid="section-parent-communication">
            <p className="eyebrow">Parent communication</p>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-3">
              You're part of the process.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Parents receive updates on their child's progress, goals, and
              suggested strategies. Parent communication is an important part of
              helping children apply skills outside the session.
            </p>
          </article>

          {/* Insurance & Payment */}
          <article id="insurance" data-testid="section-insurance">
            <p className="eyebrow">Insurance & payment</p>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-3">
              Insurance & payment information.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Nexora Learning & Development Centre (operated by MSQUARED
              Management Inc.) uses a <strong>client-pays-first</strong> model:
              families pay for services and may submit their receipts to an
              extended health insurer or another applicable payer.
            </p>
            <ul className="mt-4 grid gap-2">
              {[
                "Coverage depends on your insurance plan, the actual service provided, and the treating practitioner's qualifications.",
                "Families are responsible for verifying eligibility and reimbursement requirements with their insurer.",
                "Nexora does not guarantee insurance coverage, claim acceptance, or reimbursement.",
                "When payments are processed through Jane, Jane provides the applicable official receipt.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 rounded-2xl bg-white border border-slate-200/70 p-3.5">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-50">
                    <Check className="h-3 w-3 text-[#0369A1]" />
                  </span>
                  <span className="text-sm text-slate-700 leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </article>

          {/* Autism funding */}
          <article id="autism-funding" data-testid="section-autism-funding">
            <p className="eyebrow">Autism Funding</p>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-3">
              Using Autism Funding for this program.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Families using Autism Funding may contact us to discuss whether
              this program may fit their child's support goals. We can provide
              program descriptions, invoices, and supporting information when
              needed.
            </p>
            <p className="mt-3 text-xs text-slate-500 leading-relaxed">
              Funding eligibility depends on each family's funding situation and
              approval requirements. Nexora does not guarantee funding approval.
            </p>
          </article>

          {/* Contact */}
          <article id="contact" data-testid="section-contact">
            <p className="eyebrow">Contact</p>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mt-3">
              Ask us anything — we're happy to help.
            </h2>
            <div className="mt-5">
              <InquiryForm
                source="play_based_page"
                sourcePage="/play-based-support"
                title="Contact form"
                subtitle="Share a few details and our team will follow up shortly."
                ctaLabel="Request Information"
              />
            </div>
          </article>
        </div>

        {/* Aside */}
        <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start grid gap-4">
          <div className="rounded-[2rem] bg-slate-900 text-white p-7">
            <p className="eyebrow text-slate-400">Program at a glance</p>
            <ul className="mt-5 grid gap-3 text-sm">
              <li className="flex items-center justify-between gap-3 border-b border-white/10 pb-3">
                <span className="text-slate-400">Format</span>
                <span className="font-bold">1-on-1, in-person</span>
              </li>
              <li className="flex items-center justify-between gap-3 border-b border-white/10 pb-3">
                <span className="text-slate-400">Type</span>
                <span className="font-bold">Developmental</span>
              </li>
              <li className="flex items-center justify-between gap-3 border-b border-white/10 pb-3">
                <span className="text-slate-400">Diagnosis</span>
                <span className="font-bold">Not required</span>
              </li>
              <li className="flex items-center justify-between gap-3">
                <span className="text-slate-400">Funding</span>
                <span className="font-bold">Autism Funding welcome</span>
              </li>
            </ul>
            <div className="mt-6 grid gap-2.5">
              <TherapyBookingButton booking={booking} testId="therapy-aside-book-btn" className="w-full" />
              <Link
                to="/contact"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/30 text-white px-5 py-3 text-sm font-bold"
                data-testid="play-based-side-contact"
              >
                Request Information
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </aside>
      </section>
    </>
  );
}
